import {
  Money,
  rateLineInputSchema,
  supersedeRateLineInputSchema,
  type Currency,
  type RateBasis,
  type RateLineInput,
  type SupersedeRateLineInput,
} from '@chukua/common';

/** What a date picker hands over: only the formatting is used, so this file needs no date library. */
interface DateValue {
  format(pattern: string): string;
}

const isoDate = (value: DateValue) => value.format('YYYY-MM-DD');

export type CargoKind = 'container' | 'loose';

export interface RateLineFormValues {
  originZoneCode: string;
  destinationZoneCode: string;
  jobTypeCode: string;
  cargoKind: CargoKind;
  containerSizeCode?: string;
  containerTypeCode?: string;
  weightBandCode?: string;
  basis: RateBasis;
  amount: number;
  currency: Currency;
  emptyReturnIncluded?: boolean;
  validFrom: DateValue;
  validTo?: DateValue | null;
  reason: string;
}

export interface SupersedeFormValues {
  amount: number;
  currency: Currency;
  emptyReturnIncluded?: boolean;
  effectiveDate: DateValue;
  reason: string;
}

/** The form's own refusal, with the schema's message, so it reads like the server's and is shown the same way. */
export class InvalidRateLineInput extends Error {}

function firstMessage(issues: { message: string }[]): string {
  return issues[0]?.message ?? 'Check the fields and try again.';
}

/**
 * A container line is keyed by size and type, loose cargo by weight band, never both: a field left over from the
 * other kind is dropped here rather than sent, so switching kind mid-form can't produce a line the schema refuses.
 */
export function toRateLineInput(values: RateLineFormValues): RateLineInput {
  const isContainer = values.cargoKind === 'container';
  const parsed = rateLineInputSchema.safeParse({
    originZoneCode: values.originZoneCode,
    destinationZoneCode: values.destinationZoneCode,
    jobTypeCode: values.jobTypeCode,
    containerSizeCode: isContainer ? values.containerSizeCode : undefined,
    containerTypeCode: isContainer ? values.containerTypeCode : undefined,
    weightBandCode: isContainer ? undefined : values.weightBandCode,
    basis: values.basis,
    amount: Money.parse(String(values.amount), values.currency).toJSON(),
    emptyReturnIncluded: isContainer ? values.emptyReturnIncluded : undefined,
    validFrom: isoDate(values.validFrom),
    validTo: values.validTo ? isoDate(values.validTo) : undefined,
    reason: values.reason,
  });
  if (!parsed.success) throw new InvalidRateLineInput(firstMessage(parsed.error.issues));
  return parsed.data;
}

export function toSupersedeInput(values: SupersedeFormValues): SupersedeRateLineInput {
  const parsed = supersedeRateLineInputSchema.safeParse({
    amount: Money.parse(String(values.amount), values.currency).toJSON(),
    emptyReturnIncluded: values.emptyReturnIncluded,
    effectiveDate: isoDate(values.effectiveDate),
    reason: values.reason,
  });
  if (!parsed.success) throw new InvalidRateLineInput(firstMessage(parsed.error.issues));
  return parsed.data;
}
